import { useEffect, useMemo, useState } from "react";
import type { MailMessageRow } from "@feedback-hub/contract/browser";

import { ApiError, getMailMessages } from "../../api.js";
import { formatDate } from "../../format.js";
import type { SortDir, SortValue } from "../../sort.js";
import { ClearableInput } from "../ClearableInput.js";
import { filterMessages } from "./emailsFilter.js";
import { SortableTh } from "./SortableTh.js";
import { useSort } from "./useSort.js";

type Key = "sentAt" | "appId" | "to" | "from" | "status" | "configSet";

const DIRS: Record<Key, SortDir> = {
  sentAt: "desc",
  appId: "asc",
  to: "asc",
  from: "asc",
  status: "asc",
  configSet: "asc",
};

function value(m: MailMessageRow, key: Key): SortValue {
  return m[key];
}

const PROBLEMS: ReadonlySet<string> = new Set(["bounced", "complained", "rejected", "delayed"]);

function loadError(err: unknown): string {
  if (err instanceof ApiError) {
    return err.status === 403 ? "Only a global admin can view sent emails." : err.message;
  }
  return "Failed to load emails.";
}

export function EmailsTab() {
  const [rows, setRows] = useState<MailMessageRow[]>([]);
  const [windowFrom, setWindowFrom] = useState<string | null>(null);
  const [windowTo, setWindowTo] = useState<string | null>(null);
  const [hasOlder, setHasOlder] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [problemsOnly, setProblemsOnly] = useState(false);

  const visible = useMemo(() => filterMessages(rows, filter, problemsOnly), [rows, filter, problemsOnly]);
  const { sorted, sort, onSort } = useSort<MailMessageRow, Key>(
    visible,
    value,
    { key: "sentAt", dir: "desc" },
    DIRS,
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const page = await getMailMessages();
        if (cancelled) return;
        setRows(page.messages);
        setWindowFrom(page.from);
        setWindowTo(page.to);
        setHasOlder(page.hasOlder);
        setTruncated(page.truncated);
      } catch (err) {
        if (!cancelled) setError(loadError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function loadMore() {
    if (loadingMore || !windowFrom) return;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const page = await getMailMessages(windowFrom);
      setRows((cur) => {
        const seen = new Set(cur.map((r) => r.messageId));
        return [...cur, ...page.messages.filter((r) => !seen.has(r.messageId))];
      });
      setWindowFrom(page.from);
      setHasOlder(page.hasOlder);
      setTruncated((t) => t || page.truncated);
    } catch (err) {
      setMoreError(loadError(err));
    } finally {
      setLoadingMore(false);
    }
  }

  if (loading) return <p className="muted">Loading emails…</p>;
  if (error) return <p className="error">{error}</p>;

  const th = (label: string, k: Key) => (
    <SortableTh label={label} sortKey={k} activeKey={sort.key} dir={sort.dir} onSort={onSort} />
  );

  return (
    <div className="panel section">
      <h3>Recent emails</h3>
      <div className="emails-controls">
        <ClearableInput
          type="search"
          value={filter}
          onChange={setFilter}
          placeholder="Filter"
          aria-label="Filter emails"
        />
        <label>
          <input
            type="checkbox"
            checked={problemsOnly}
            onChange={(e) => setProblemsOnly(e.target.checked)}
          />{" "}
          Problems only
        </label>
      </div>

      {sorted.length === 0 ? (
        <p className="muted">No emails recorded in this window.</p>
      ) : (
        <div className="table-wrap">
          <table className="queue">
            <thead>
              <tr>
                {th("Sent", "sentAt")}
                {th("App", "appId")}
                {th("To", "to")}
                {th("From", "from")}
                {th("Status", "status")}
                {th("Config set", "configSet")}
              </tr>
            </thead>
            <tbody>
              {sorted.map((m) => (
                <tr key={m.messageId} style={{ cursor: "default" }}>
                  <td>{formatDate(m.sentAt)}</td>
                  <td className="mono">{m.appId}</td>
                  <td className="mono">{m.to}</td>
                  <td className="mono">{m.from ?? "—"}</td>
                  <td>
                    <span
                      className={PROBLEMS.has(m.status) ? "warn-mark" : undefined}
                      style={{ marginLeft: 0 }}
                    >
                      {m.status}
                      {m.bounceType ? ` · ${m.bounceType}` : ""}
                    </span>
                  </td>
                  <td className="mono">{m.configSet}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {windowFrom && windowTo && (
        <p className="muted">
          Showing {sorted.length} of {rows.length} emails sent {formatDate(windowFrom)} –{" "}
          {formatDate(windowTo)}.
        </p>
      )}
      {truncated && (
        <p className="muted">
          Some windows had more than 1,000 emails; only the newest 1,000 of each are shown.
        </p>
      )}
      {hasOlder && (
        <button
          type="button"
          disabled={loadingMore}
          onClick={() => void loadMore()}
          style={{ minWidth: "11rem" }}
        >
          {loadingMore && <span className="btn-spinner" aria-hidden="true" />}
          Load 30 more days
        </button>
      )}
      {moreError && <p className="error">{moreError}</p>}
    </div>
  );
}
